/**
 * @file Fonctions geometriques 2D pures (distances, angles, segment/cercle) utilisees par collisions et ciblage.
 */
import type { Vec2 } from '@shared/types';

export const dist2 = (ax: number, ay: number, bx: number, by: number): number => {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
};
export const dist = (ax: number, ay: number, bx: number, by: number): number => Math.sqrt(dist2(ax, ay, bx, by));
export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const angleTo = (fromX: number, fromY: number, toX: number, toY: number): number => Math.atan2(toY - fromY, toX - fromX);
export const DEG = Math.PI / 180;

/** Plus petit ecart angulaire signe a -> b dans [-PI, PI]. */
export const angleDiff = (a: number, b: number): number => {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
};

/** Tourne `from` vers `to` d'au plus `maxStep` radians. */
export const rotateToward = (from: number, to: number, maxStep: number): number => {
  const d = angleDiff(from, to);
  return Math.abs(d) <= maxStep ? to : from + Math.sign(d) * maxStep;
};

export const normalize = (x: number, y: number, out: Vec2): Vec2 => {
  const l = Math.hypot(x, y);
  if (l < 1e-9) {
    out.x = 0;
    out.y = 0;
  } else {
    out.x = x / l;
    out.y = y / l;
  }
  return out;
};

/**
 * Parametre t (0..1) de la 1re intersection segment [A,B] / cercle (C,r), ou -1 si aucune.
 * Utilise par les projectiles (anti-tunneling : on teste le segment parcouru pendant le tic) et les faisceaux.
 */
export const segmentCircleHit = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number, r: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const fx = ax - cx;
  const fy = ay - cy;
  const c = fx * fx + fy * fy - r * r;
  if (c <= 0) return 0; // depart a l'interieur du cercle
  const a = dx * dx + dy * dy;
  if (a < 1e-12) return -1;
  const b = 2 * (fx * dx + fy * dy);
  const disc = b * b - 4 * a * c;
  if (disc < 0) return -1;
  const t = (-b - Math.sqrt(disc)) / (2 * a);
  return t >= 0 && t <= 1 ? t : -1;
};

/** Distance point -> segment (largeur des faisceaux laser). */
export const pointSegmentDist = (px: number, py: number, ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const l2 = dx * dx + dy * dy;
  const t = l2 < 1e-12 ? 0 : clamp(((px - ax) * dx + (py - ay) * dy) / l2, 0, 1);
  return dist(px, py, ax + dx * t, ay + dy * t);
};

/** Point de l'arene le plus proche (rect centre en 0, cercle de rayon halfW, couloir |x| <= halfW infini en y). */
export const clampToArena = (p: Vec2, shape: 'rect' | 'circle' | 'corridor', halfW: number, halfH: number, margin = 0): Vec2 => {
  if (shape === 'corridor') {
    p.x = clamp(p.x, -halfW + margin, halfW - margin);
  } else if (shape === 'rect') {
    p.x = clamp(p.x, -halfW + margin, halfW - margin);
    p.y = clamp(p.y, -halfH + margin, halfH - margin);
  } else {
    const r = halfW - margin;
    const l = Math.hypot(p.x, p.y);
    if (l > r && l > 0) {
      p.x *= r / l;
      p.y *= r / l;
    }
  }
  return p;
};
